/*
 * File: DeploymentRequest.ts
 * Project: rosita
 * -----
 * Last Modified: 21 Feb 2022 1:13:30 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { RcFile, UploadFile } from 'antd/lib/upload/interface';
import * as yup from 'yup';
import { Address, addressSchema } from './Address';
import { Contact, contactSchema } from './Contact';
import { ProductPayload, productSchema } from './Product';
import { Project, projectSchema } from './Project';

export interface DeploymentRequest {
  attachments: UploadFile[];

  business: BusinessReference;
  contact: Contact;
  createdByFullName: string;
  createdByUserId: string;
  createdDateTime: string;
  customerReferenceNumber: string | null;
  division: DivisionReference;
  // TODO: Turn into a ENUM
  documentType: string;
  erpOrderId: string | null;
  id: string;
  installAddress: Address | null;
  isActive: boolean;
  isDeleted: boolean;
  isOrderReleased: boolean;
  isPartialShipments: boolean;
  modifiedByFullName: string;
  modifiedByUserId: string;
  modifiedDateTime: string;
  note: string | null;
  notificationEmails: string[];
  partitionKey: string;
  partitionKeyDescription: string;
  products: ProductPayload[];
  // TODO: Remove undefined on project name when backend support made
  project?: Project;
  requestedDeliveryDate: string | null;
  requestNumber: string;
  shippingAddress: Address | null;
  status: DeploymentStatusesLabels;
  transactionType: string;
}

export interface BusinessReference {
  dataAreaId: string;
  erpId: string;
  id: string;
}

export interface DivisionReference {
  id: string;
  erpId: string;
  name: string;
}

export enum DeploymentStatuses {
  cancelled = 'cancelled',
  delivered = 'delivered',
  draft = 'draft',
  invoiced = 'invoiced',
  myOrders = 'myOrders',
  orderCreated = 'orderCreated',
  pendingApproval = 'pendingApproval',
  pendingTierTwoApproval = 'pendingTierTwoApproval',
  rejected = 'rejected',
  rejectedTierTwoApproval = 'rejectedTierTwoApproval',
  shipped = 'shipped',
  submitted = 'submitted'
}

export enum DeploymentStatusesLabels {
  cancelled = 'Cancelled',
  delivered = 'Delivered',
  draft = 'Draft',
  invoiced = 'Invoiced',
  myOrders = 'MyOrders',
  orderCreated = 'OrderCreated',
  pendingApproval = 'PendingApproval',
  pendingTierTwoApproval = 'PendingTierTwoApproval',
  rejected = 'Rejected',
  rejectedTierTwoApproval = 'RejectedTierTwoApproval',
  shipped = 'Shipped',
  submitted = 'Submitted'
}

export interface DeploymentRequestParams {
  offset: number;
  requester?: string;
  referenceNumberContains?: string;
  orderNumberContains?: string;
  requestNumberContains?: string;
  startDate?: string;
  endDate?: string;
  statuses?: DeploymentStatuses[];
  includeInactiveData?: boolean;
  includeDeletedData?: boolean;
  returnDeletedDataOnly?: boolean;
}

export enum DeploymentRequestsModes {
  view = 'view',
  recover = 'recover'
}

export interface DeploymentRequestPayload {
  // Request Details
  requestNumber?: string;
  requestNumberSuffix?: string;
  customerReferenceNumber?: string;
  // Install Location
  installAddress: Address | null;
  // Products
  products: ProductPayload[];
  // Shipping Location
  shippingAddress: Address | null;
  // Shipping Details
  contact: Contact;
  requestedDeliveryDate: string;
  // More Details
  notificationEmails: string[];
  note?: string;

  project: Project | null;
  // Status
  status: DeploymentStatuses;
  isOrderReleased: boolean;
  // What's up with this
  isPartialShipments: boolean;
  // Attachments
  attachments: RcFile[];
}

export enum DeploymentRequestLabels {
  // Request Details
  requestNumber = 'Request Number',
  customerReferenceNumber = 'Reference Number',
  // Install Location
  installAddress = 'Install Location',
  // Products
  products = 'Products',
  // Shipping Location
  shippingAddress = 'Shipping Location',
  // Shipping Details
  contact = 'Ship to Contact',
  requestedDeliveryDate = 'Requested Delivery Date',
  // More Details
  notificationEmails = 'Notification Subscriptions',
  note = 'Note',

  project = 'Project',
  status = 'Status',
  isOrderReleased = '',
  // What's up with this
  isPartialShipments = 'Allow for Partial Shipments'
}

export const deploymentRequestPayloadSchema: yup.SchemaOf<DeploymentRequestPayload> = yup.object({
  requestNumber: yup.string().label(DeploymentRequestLabels.requestNumber),
  requestNumberSuffix: yup.string().label(DeploymentRequestLabels.requestNumber + ' Suffix'),
  customerReferenceNumber: yup.string().label(DeploymentRequestLabels.customerReferenceNumber),
  installAddress: addressSchema.nullable(),
  products: yup.array(productSchema),
  shippingAddress: addressSchema.nullable(),
  contact: contactSchema,
  requestedDeliveryDate: yup.string().label(DeploymentRequestLabels.requestedDeliveryDate).required(),
  notificationEmails: yup.array(yup.string().required().email()).default([]),
  note: yup.string(),
  project: projectSchema.nullable(),
  status: yup.mixed().oneOf(Object.values(DeploymentStatuses)).required().default(DeploymentStatuses.draft),
  isOrderReleased: yup.boolean().required().default(false),
  isPartialShipments: yup.boolean().required().default(false),
  attachments: yup.array()
});
